import type { Dispatch, Middleware, UnknownAction } from '@reduxjs/toolkit';

import { opsManagementApi } from '@/features/admin/api/opsManagementApi';
import {
  batchTelemetryStationUpdate,
  iesAlertReceived,
} from '@/features/realtime/stores/realtimeSlice';
import { isBulkAlertAckInProgress } from '@/features/realtime/utils/acknowledgeOpenIotAlerts';
import { normalizeGatewayRealtimeAlertPayload } from '@/features/realtime/utils/gatewayRealtimePayload';
import {
  normalizeGatewayAlertEventPayload,
  normalizeGatewayDashboardKpiPayload,
} from '@/features/realtime/utils/gatewayWsKpiPayload';
import { normalizeIotTelemetryPayload } from '@/features/realtime/utils/iotTelemetryPayload';
import { socketManager } from '@/lib/socket';

export interface SocketAction {
  type: string;
  payload?: unknown;
}

/** Subscription đồng bộ từ dashboard. */
const syncedStationIds = new Set<string>();
const syncedBasinIds = new Set<string>();
/** Trạm thêm qua `socket/subscribeStation` — không bị sync xóa. */
const extraStationIds = new Set<string>();

const subscribedStationIdsOnWire = new Set<string>();
const subscribedBasinIdsOnWire = new Set<string>();

const ALERT_HISTORY_INVALIDATE_DEBOUNCE_MS = 500;
let alertHistoryInvalidateTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleAlertHistoryInvalidate(dispatch: Dispatch<UnknownAction>): void {
  if (alertHistoryInvalidateTimer) clearTimeout(alertHistoryInvalidateTimer);
  alertHistoryInvalidateTimer = setTimeout(() => {
    alertHistoryInvalidateTimer = null;
    if (isBulkAlertAckInProgress()) {
      scheduleAlertHistoryInvalidate(dispatch);
      return;
    }
    dispatch(
      opsManagementApi.util.invalidateTags([
        { type: 'OpsAlert', id: 'HISTORY' },
        { type: 'ReportSummary', id: 'DASHBOARD' },
      ]),
    );
  }, ALERT_HISTORY_INVALIDATE_DEBOUNCE_MS);
}

function desiredStationIds(): Set<string> {
  return new Set([...syncedStationIds, ...extraStationIds]);
}

function flushStationSubscriptions(): void {
  const wanted = desiredStationIds();
  for (const id of subscribedStationIdsOnWire) {
    if (!wanted.has(id)) {
      socketManager.unsubscribeStation(id);
      subscribedStationIdsOnWire.delete(id);
    }
  }
  for (const id of wanted) {
    if (!subscribedStationIdsOnWire.has(id)) {
      socketManager.subscribeStation(id);
      subscribedStationIdsOnWire.add(id);
    }
  }
}

function flushBasinSubscriptions(): void {
  for (const id of subscribedBasinIdsOnWire) {
    if (!syncedBasinIds.has(id)) {
      socketManager.unsubscribeBasin(id);
      subscribedBasinIdsOnWire.delete(id);
    }
  }
  for (const id of syncedBasinIds) {
    if (!subscribedBasinIdsOnWire.has(id)) {
      socketManager.subscribeBasin(id);
      subscribedBasinIdsOnWire.add(id);
    }
  }
}

function flushAllRealtimeSubscriptions(): void {
  flushStationSubscriptions();
  flushBasinSubscriptions();
}

export const socketMiddleware: Middleware = (store) => {
  let initialized = false;
  let realtimeListenersAttached = false;

  const dispatchTelemetry = (data: unknown) => {
    const patches = normalizeIotTelemetryPayload(data);
    if (patches.length > 0) {
      store.dispatch(batchTelemetryStationUpdate(patches));
    }
  };

  function attachRealtimeSocketListeners(): void {
    if (realtimeListenersAttached) return;
    realtimeListenersAttached = true;

    socketManager.on('map:layer-update', (data: unknown) => {
      store.dispatch({ type: 'map/layerDataUpdated', payload: data });
    });

    socketManager.on('sensor_updated', (data: unknown) => dispatchTelemetry(data));
    socketManager.on('water_level', (raw: unknown) => dispatchTelemetry(raw));
    socketManager.on('rainfall', (raw: unknown) => dispatchTelemetry(raw));

    socketManager.on('ies_alert', (raw: unknown) => {
      const alert = normalizeGatewayRealtimeAlertPayload(raw);
      if (alert) store.dispatch(iesAlertReceived(alert));
    });

    socketManager.on('alert_event', (raw: unknown) => {
      const event = normalizeGatewayAlertEventPayload(raw);
      if (!event) return;
      if (isBulkAlertAckInProgress()) return;
      // Debounce — BE có thể bắn nhiều `alert_event` liên tiếp sau PATCH ack.
      scheduleAlertHistoryInvalidate(store.dispatch);
    });

    socketManager.on('dashboard_kpi', (raw: unknown) => {
      const kpi = normalizeGatewayDashboardKpiPayload(raw);
      if (!kpi) return;
      store.dispatch(
        opsManagementApi.util.invalidateTags([{ type: 'ReportSummary', id: 'DASHBOARD' }]),
      );
    });

    socketManager.on('connect', () => {
      store.dispatch({ type: 'realtime/connected' });
      flushAllRealtimeSubscriptions();
    });

    socketManager.on('disconnect', () => {
      store.dispatch({ type: 'realtime/disconnected' });
    });
  }

  return (next) => (action) => {
    const typedAction = action as SocketAction;

    if (typedAction.type === 'socket/connect' && !initialized) {
      const token = (store.getState() as { auth?: { token?: string } }).auth?.token;
      socketManager.connect(token);
      attachRealtimeSocketListeners();
      initialized = true;
    }

    if (typedAction.type === 'socket/disconnect') {
      for (const id of subscribedStationIdsOnWire) {
        socketManager.unsubscribeStation(id);
      }
      for (const id of subscribedBasinIdsOnWire) {
        socketManager.unsubscribeBasin(id);
      }
      subscribedStationIdsOnWire.clear();
      subscribedBasinIdsOnWire.clear();
      syncedStationIds.clear();
      syncedBasinIds.clear();
      extraStationIds.clear();
      socketManager.disconnect();
      initialized = false;
      realtimeListenersAttached = false;
    }

    if (typedAction.type === 'socket/syncRealtimeSubscriptions') {
      const p = typedAction.payload as { stationIds?: string[]; basinIds?: string[] } | undefined;
      syncedStationIds.clear();
      syncedBasinIds.clear();
      for (const id of p?.stationIds ?? []) {
        const s = String(id).trim();
        if (s) syncedStationIds.add(s);
      }
      for (const id of p?.basinIds ?? []) {
        const s = String(id).trim();
        if (s) syncedBasinIds.add(s);
      }
      if (socketManager.isConnected) {
        flushAllRealtimeSubscriptions();
      }
    }

    if (typedAction.type === 'socket/subscribeStation') {
      const id = String(typedAction.payload ?? '').trim();
      if (id) {
        extraStationIds.add(id);
        if (socketManager.isConnected) flushStationSubscriptions();
      }
    }

    if (typedAction.type === 'socket/unsubscribeStation') {
      const id = String(typedAction.payload ?? '').trim();
      if (id && extraStationIds.delete(id) && socketManager.isConnected) {
        flushStationSubscriptions();
      }
    }

    return next(action);
  };
};
